import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Users, Layers, TrendingDown, AlertCircle } from 'lucide-react';
import Section from './Section';

const painPoints = [
  { icon: Clock, text: 'Rewriting content 5x for each platform', color: 'text-rose-500' },
  { icon: Users, text: 'No bandwidth to post consistently', color: 'text-amber-500' },
  { icon: Layers, text: 'Platform overload without strategy', color: 'text-indigo-500' },
  { icon: TrendingDown, text: 'Inconsistent brand voice across channels', color: 'text-violet-500' },
];

const Problem = () => {
  return (
    <Section id="problem" background="alt" data-testid="problem-section">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-rose-50 rounded-full text-sm font-medium text-rose-600 mb-4">
            <AlertCircle className="w-4 h-4" />
            The Problem
          </span>
          <h2 
            className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
            style={{ textWrap: 'balance' }}
          >
            Content Distribution Is Killing Your Growth
          </h2>
          <p 
            className="text-slate-600 text-lg max-w-2xl mx-auto"
          >
            SaaS teams spend more time reformatting content than creating it.<br />Sound familiar?
          </p>
        </motion.div>

        {/* Pain Points Grid */}
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          {painPoints.map((point, index) => (
            <motion.div
              key={point.text}
              className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-slate-200 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <div className={`w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center ${point.color}`}>
                <point.icon className="w-5 h-5" />
              </div>
              <p className="text-slate-700 font-medium">{point.text}</p>
            </motion.div>
          ))}
        </div>

        {/* Bottom Statement */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <p className="text-xl md:text-2xl font-semibold text-slate-800">
            One piece of content.<br className="md:hidden" />{' '}Five different versions.
          </p>
          <p className="text-slate-500 mt-2">It doesn't have to work this way.</p>
        </motion.div>
      </div>
    </Section>
  );
};

export default Problem;
